// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface GraphicsPackageLevel {
  id: string;
  name: string;
  slug: string;
  base_price: number;
  max_proofs: number;
  max_revisions: number;
  turnaround_days: number;
  description: string;
  features: string[];
  is_active: boolean;
  display_order: number;
}

const GraphicsPackageLevelsTab: React.FC = () => {
  const [levels, setLevels] = useState<GraphicsPackageLevel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLevel, setEditingLevel] = useState<GraphicsPackageLevel | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    base_price: 0,
    max_proofs: 3,
    max_revisions: 2,
    turnaround_days: 7,
    description: '',
    features: '',
    display_order: 0
  });

  useEffect(() => {
    fetchLevels();
  }, []);

  const fetchLevels = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await (supabase as any)
        .from('graphics_package_levels')
        .select('*')
        .order('display_order');

      if (error) throw error;
      setLevels((data as any) || []);
    } catch (error: any) {
      console.error('Error fetching package levels:', error);
      toast.error('Failed to load package levels');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (level: GraphicsPackageLevel) => {
    setEditingLevel(level);
    setFormData({
      name: level.name,
      slug: level.slug,
      base_price: level.base_price,
      max_proofs: level.max_proofs,
      max_revisions: level.max_revisions,
      turnaround_days: level.turnaround_days,
      description: level.description || '',
      features: level.features.join('\n'),
      display_order: level.display_order
    });
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingLevel(null);
    setFormData({
      name: '',
      slug: '',
      base_price: 0,
      max_proofs: 3,
      max_revisions: 2,
      turnaround_days: 7,
      description: '',
      features: '',
      display_order: levels.length + 1
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    try {
      const features = formData.features
        .split('\n')
        .filter(f => f.trim())
        .map(f => f.trim());

      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-');

      if (editingLevel) {
        const { error } = await (supabase as any)
          .from('graphics_package_levels')
          .update({
            name: formData.name,
            slug,
            base_price: formData.base_price,
            max_proofs: formData.max_proofs,
            max_revisions: formData.max_revisions,
            turnaround_days: formData.turnaround_days,
            description: formData.description,
            features,
            display_order: formData.display_order
          })
          .eq('id', editingLevel.id);

        if (error) throw error;
        toast.success('Package level updated successfully');
      } else {
        const { error } = await (supabase as any)
          .from('graphics_package_levels')
          .insert({
            name: formData.name,
            slug,
            base_price: formData.base_price,
            max_proofs: formData.max_proofs,
            max_revisions: formData.max_revisions,
            turnaround_days: formData.turnaround_days,
            description: formData.description,
            features,
            display_order: formData.display_order
          });

        if (error) throw error;
        toast.success('Package level created successfully');
      }

      setShowForm(false);
      fetchLevels();
    } catch (error: any) {
      console.error('Error saving package level:', error);
      toast.error('Failed to save package level');
    }
  };

  const handleDelete = async (level: GraphicsPackageLevel) => {
    if (!confirm(`Are you sure you want to delete the ${level.name} level?`)) return;

    try {
      const { error } = await (supabase as any)
        .from('graphics_package_levels')
        .delete()
        .eq('id', level.id);

      if (error) throw error;
      toast.success('Package level deleted successfully');
      fetchLevels();
    } catch (error: any) {
      console.error('Error deleting package level:', error);
      toast.error('Failed to delete package level');
    }
  };

  const toggleActive = async (level: GraphicsPackageLevel) => {
    try {
      const { error } = await (supabase as any)
        .from('graphics_package_levels')
        .update({ is_active: !level.is_active })
        .eq('id', level.id);

      if (error) throw error;
      toast.success(`Package level ${level.is_active ? 'deactivated' : 'activated'}`);
      fetchLevels();
    } catch (error: any) {
      console.error('Error toggling level status:', error);
      toast.error('Failed to update level status');
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-6">Loading package levels...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Package Levels</h3>
          <p className="text-sm text-muted-foreground">
            Manage graphics package levels (Bronze, Silver, Gold, etc.)
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Level
        </Button>
      </div>

      <div className="grid gap-4">
        {levels.map((level) => (
          <Card key={level.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <h4 className="font-semibold text-lg">{level.name}</h4>
                  <Badge variant={level.is_active ? "default" : "secondary"}>
                    {level.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => toggleActive(level)}
                  >
                    {level.is_active ? 'Deactivate' : 'Activate'}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(level)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleDelete(level)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Base Price</label>
                  <p className="text-lg font-semibold">${level.base_price}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Max Proofs</label>
                  <p className="text-lg font-semibold">{level.max_proofs}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Max Revisions</label>
                  <p className="text-lg font-semibold">{level.max_revisions}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Turnaround</label>
                  <p className="text-lg font-semibold">{level.turnaround_days} days</p>
                </div>
              </div>

              {level.description && (
                <p className="text-sm text-muted-foreground mb-3">{level.description}</p>
              )}

              {level.features.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Features:</label>
                  <ul className="text-sm mt-1 space-y-1">
                    {level.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <span className="w-1 h-1 bg-primary rounded-full"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {levels.length === 0 && (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">No package levels found.</p>
              <Button className="mt-4" onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Create First Level
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingLevel ? 'Edit Package Level' : 'Create Package Level'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Name</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Premium"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Slug</label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="e.g., premium"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Base Price ($)</label>
                <Input
                  type="number"
                  value={formData.base_price}
                  onChange={(e) => setFormData({ ...formData, base_price: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Display Order</label>
                <Input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Max Proofs</label>
                <Input
                  type="number"
                  value={formData.max_proofs}
                  onChange={(e) => setFormData({ ...formData, max_proofs: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Max Revisions</label>
                <Input
                  type="number"
                  value={formData.max_revisions}
                  onChange={(e) => setFormData({ ...formData, max_revisions: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Turnaround (days)</label>
                <Input
                  type="number"
                  value={formData.turnaround_days}
                  onChange={(e) => setFormData({ ...formData, turnaround_days: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this package level"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Features (one per line)</label>
              <Textarea
                value={formData.features}
                onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                placeholder="High-resolution final files
Social media formats
Print-ready files"
                rows={4}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end mt-6">
            <Button variant="outline" onClick={() => setShowForm(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              <Save className="h-4 w-4 mr-2" />
              {editingLevel ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GraphicsPackageLevelsTab;